export function trackEvent(eventName: string, eventParams: Record<string, string | number | boolean> = {}) {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', eventName, eventParams);
  }
}

export function trackFormSubmission(
  formId: 'contact' | 'offerte' | 'homepage-contact',
  extraParams: Record<string, string | number | boolean> = {}
) {
  const timestamp = new Date().toISOString();

  // Primary lead event
  trackEvent('generate_lead', {
    event_category: 'form_submission',
    event_label: formId,
    form_id: formId,
    form_type: formId === 'contact' ? 'contact' : 'offerte',
    submission_timestamp: timestamp,
    ...extraParams,
  });

  // Dedicated conversion event tagged with form type, timestamp, vehicle category
  trackEvent('form_conversion', {
    form_id: formId,
    form_type: formId === 'contact' ? 'contact' : 'offerte',
    vehicle_category: (extraParams.merk as string) || (extraParams.dienst as string) || 'onbekend',
    submission_timestamp: timestamp,
    ...extraParams,
  });

  // GA4 conversion ping
  trackEvent('conversion', {
    send_to: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '',
    event_category: 'lead',
    form_id: formId,
    form_type: formId === 'contact' ? 'contact' : 'offerte',
    submission_timestamp: timestamp,
    ...extraParams,
  });
}

/**
 * Track phone or WhatsApp CTA button clicks for conversion measurement.
 * @param type - 'phone' | 'whatsapp' * @param location - page or component where the click occurred (e.g.'header', 'homepage-hero', 'contact-page')
 * @param extraParams - optional additional GA params (e.g. service, city)
 */
export function trackCTAClick(
  type: 'phone' | 'whatsapp',
  location: string,
  extraParams: Record<string, string | number | boolean> = {}
) {
  trackEvent('cta_click', {
    event_category: 'cta',
    event_label: `${type}_${location}`,
    cta_type: type,
    cta_location: location,
    ...extraParams,
  });

  // Also fire a dedicated contact_click event for GA4 funnel analysis
  trackEvent('contact_click', {
    method: type,
    location,
    ...extraParams,
  });
}

/**
 * Track city page CTA clicks (phone, whatsapp, offerte) with location slug attribution.
 */
export function trackCityCTAClick(
  type: 'phone' | 'whatsapp' | 'offerte',
  citySlug: string,
  cityName: string,
  ctaPosition: 'hero' | 'cta-section' | 'sidebar' = 'hero'
) {
  trackEvent('city_cta_click', {
    event_category: 'city_conversion',
    event_label: `${type}_${citySlug}`,
    cta_type: type,
    city_slug: citySlug,
    city_name: cityName,
    cta_position: ctaPosition,
  });
}

/**
 * Track diagnosis request clicks on city pages for revenue attribution.
 */
export function trackDiagnosisRequest(
  citySlug: string,
  cityName: string,
  source: 'city-page' | 'sidebar' | 'services-section' = 'city-page'
) {
  trackEvent('diagnosis_request', {
    event_category: 'city_conversion',
    event_label: `diagnosis_${citySlug}`,
    city_slug: citySlug,
    city_name: cityName,
    request_source: source,
  });

  // Also fire generate_lead for GA4 funnel
  trackEvent('generate_lead', {
    event_category: 'diagnosis',
    event_label: citySlug,
    city_slug: citySlug,
    city_name: cityName,
    lead_type: 'diagnosis_request',
  });
}

/**
 * Track form submissions on city pages with location slug attribution.
 */
export function trackCityFormSubmission(
  formId: 'contact' | 'offerte' | 'homepage-contact',
  citySlug: string,
  cityName: string,
  extraParams: Record<string, string | number | boolean> = {}
) {
  const timestamp = new Date().toISOString();

  trackEvent('city_form_submission', {
    event_category: 'city_conversion',
    event_label: `${formId}_${citySlug}`,
    form_id: formId,
    city_slug: citySlug,
    city_name: cityName,
    submission_timestamp: timestamp,
    ...extraParams,
  });

  // Also fire the standard generate_lead for unified funnel reporting
  trackFormSubmission(formId, {
    city_slug: citySlug,
    city_name: cityName,
    ...extraParams,
  });
}
